import { today } from '@seen/core/dates'
import {
  AdmissionEncounter,
  type AdmissionStatus,
} from '@seen/core/models/encounters/admission-encounter'
import { getReferenceString } from '@seen/core/references'
import { Button } from '@seen/ui'
import { type JSX, useEffect } from 'react'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { LocationComboboxButton } from '#src/components/LocationComboboxButton.tsx'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useUpsertResourceFormDialogStore } from '#src/hooks/useUpsertResourceFormDialogStore.ts'

interface EditAdmissionProps {
  admission: AdmissionEncounter
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditAdmission({
  admission,
  open,
  onOpenChange,
}: EditAdmissionProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const patch = usePatchMutation<AdmissionEncounter>()
  const openUpsertResourceFormDialog = useUpsertResourceFormDialogStore(
    (state) => state.open
  )

  const form = useForm({
    schema: z
      .object({
        location: z.string({ required_error: 'Location is required' }),
        startTime: z.string({ required_error: 'Start date is required' }),
        endTime: z.string().nullable(),
        status: z.enum(AdmissionEncounter.ADMISSION_STATUS_VALUES, {
          required_error: 'Status is required',
        }),
      })
      .superRefine((data, ctx) => {
        if (data.endTime && new Date(data.endTime) < new Date(data.startTime)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be after start date',
            path: ['endTime'],
          })
        }
      }),
    defaultValues: {
      location: admission.location?.reference ?? '',
      startTime: admission.startTime ?? today(),
      endTime: admission.endTime ?? null,
      status: AdmissionEncounter.isValidStatus(admission.status)
        ? admission.status
        : 'planned',
    },
  })

  useEffect(() => {
    form.reset({
      location: admission.location?.reference ?? '',
      startTime: admission.startTime ?? today(),
      endTime: admission.endTime ?? null,
      status: AdmissionEncounter.isValidStatus(admission.status)
        ? admission.status
        : 'planned',
    })
  }, [admission, form])

  const submit = form.handleSubmit((values) => {
    patch.mutate(
      {
        instance: admission,
        mutateFn: (admission) => {
          admission.location = { reference: values.location }
          admission.startTime = values.startTime
          if (admission.endTime || values.endTime) {
            admission.endTime = values.endTime ?? undefined
          }
          admission.status = values.status
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          form.reset()
        },
      }
    )
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader className="py-4 text-lg">Edit Admission</DialogHeader>
        <DialogContent className="pt-0">
          <Form onSubmit={submit}>
            <div className="space-y-8">
              <div className="flex flex-1 gap-4">
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">
                    Location
                  </FormLabel>
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field: { value, onChange, ...fieldRest } }) => (
                      <LocationComboboxButton
                        {...fieldRest}
                        value={value ? { reference: value } : undefined}
                        onChange={(value) => onChange(value?.reference ?? '')}
                        onEdit={(l) => {
                          openUpsertResourceFormDialog({
                            resource: l.toFhirResource(),
                            title: 'Edit Location',
                            onSubmit: (r) => {
                              onChange(getReferenceString(r))
                            },
                          })
                        }}
                        onCreate={() => {
                          openUpsertResourceFormDialog({
                            resource: { resourceType: 'Location' },
                            title: 'Create Location',
                            onSubmit: (r) => {
                              onChange(getReferenceString(r))
                            },
                          })
                        }}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">
                    Start Date
                  </FormLabel>
                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        onChange={(value) => field.onChange(value ?? '')}
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">
                    End Date
                  </FormLabel>
                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <DatePicker {...field} allowUndefined />
                    )}
                  />
                </div>
                <div className="flex flex-col">
                  <FormLabel className="text-sm font-medium">Status</FormLabel>
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <Combobox<AdmissionStatus>
                        options={AdmissionEncounter.ADMISSION_STATUS_OPTIONS}
                        onSelect={field.onChange}
                      >
                        <Button variant="outline">{field.value}</Button>
                      </Combobox>
                    )}
                  />
                </div>
              </div>
              <Button
                type="submit"
                loading={patch.isPending}
                disabled={isReadOnly}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
